import React from 'react'
import {
  Checkbox,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Typography,
  SxProps,
  Theme,
} from '@mui/material'
import {CONTROL_HEIGHT, NEUTRAL_SCALE} from 'src/theme-new/tokens'

interface Option {
  value: string
  label: string
}

interface MultiSelectNewProps {
  value: string[]
  onChange: (value: string[]) => void
  options: Option[]
  label?: string
  required?: boolean
  disabled?: boolean
  error?: boolean
  helperText?: string
  fullWidth?: boolean
  sx?: SxProps<Theme>
}

export default function MultiSelectNew({
  value,
  onChange,
  options,
  label,
  required,
  disabled,
  error,
  helperText,
  fullWidth,
  sx,
}: MultiSelectNewProps): JSX.Element {
  const handleChange = (e: SelectChangeEvent<string[]>) => {
    onChange(e.target.value as string[])
  }

  const renderValue = (selected: string[]) =>
    selected.map(v => options.find(o => o.value === v)?.label ?? v).join(', ')

  return (
    <Stack spacing={0.5} sx={{width: fullWidth ? '100%' : undefined, ...sx}}>
      {label && (
        <Typography variant="caption" sx={{fontWeight: 600, color: NEUTRAL_SCALE[700], textTransform: 'uppercase', letterSpacing: '0.05em'}}>
          {label}{required && ' *'}
        </Typography>
      )}
      <Select
        size="small"
        multiple
        value={value}
        onChange={handleChange}
        disabled={disabled}
        error={error}
        displayEmpty
        fullWidth={fullWidth}
        renderValue={(selected) => renderValue(selected as string[])}
        sx={{
          height: CONTROL_HEIGHT,
          borderRadius: '8px',
          backgroundColor: '#fff',
          '& fieldset': {borderColor: error ? 'error.main' : NEUTRAL_SCALE[200]},
          '&:hover fieldset': {borderColor: error ? 'error.main' : NEUTRAL_SCALE[300]},
          '&.Mui-focused fieldset': {borderColor: error ? 'error.main' : 'primary.main'},
          '&.Mui-disabled': {opacity: 0.6},
        }}
      >
        {options.map(opt => (
          <MenuItem key={opt.value} value={opt.value}>
            <Checkbox size="small" checked={value.includes(opt.value)} sx={{p: '4px', mr: 0.5}} />
            {opt.label}
          </MenuItem>
        ))}
      </Select>
      {helperText && (
        <Typography variant="caption" sx={{color: error ? 'error.main' : NEUTRAL_SCALE[500]}}>
          {helperText}
        </Typography>
      )}
    </Stack>
  )
}
